import React, { useState, useEffect } from "react";
import { UpgradeButton } from "@/components/shared/UpgradeButton";
import { useNavigate } from "react-router-dom";
import { X, Loader2, LogOut, CheckCircle2, AlertTriangle, CreditCard, Save, BarChart3, ShieldCheck, KeyRound, Clock, Download, Trash2, Lock, Bell, BellOff, Mail, Smartphone } from "lucide-react";
import { AiIcon } from "@/components/ui/ai-icon";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { AvatarPicker } from "@/components/AvatarPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const CAREER_LEVELS = ["entry", "mid", "senior", "manager", "executive"];
const SUMMARY_STYLES = [
  { key: "short", label: "Short" },
  { key: "executive", label: "Executive" },
  { key: "technical", label: "Technical" },
  { key: "ats_optimized", label: "ATS Optimized" },
];
// Fallback only. /api/ai/usage now ships its own `labels` map (ai-service's
// FEATURE_LABELS, which is maintained right next to TIER_QUOTAS), and that
// wins -- keeping the authoritative copy on the server is what stops this
// list drifting behind newly added quotas the way it already did once.
// Retained so a cached bundle talking to an older ai-service still renders
// real names rather than raw keys.
const USAGE_LABELS = {
  resume_parse: "Resume generations (paste)",
  resume_parse_file: "Resume generations (upload)",
  ats_score: "ATS scans",
  job_match: "Job match analyses",
  cover_letter: "Cover letters",
  linkedin_optimize: "LinkedIn rewrites",
  linkedin_generate: "LinkedIn profile generation",
  suggest_template: "Style suggestions",
  ai_suggest: "AI polish (summary/bullets)",
  grammar_fix: "Grammar & spelling fixes",
  job_search_suggestions: "Job search suggestions",
  cover_letter_teaser: "Cover letter previews",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

// Fallback only -- /me/notification-preferences now ships `labels`
// (notifications-service's CATEGORY_LABELS, kept beside CATEGORIES), and that
// wins. This copy had drifted a category behind the server and, being read
// with no fallback, rendered "activity" as an empty label next to a live
// toggle. "job_matches" no longer claims a percentage: alerts fire for strong
// matches AND lower-scoring improvable ones, and both bars are env-tunable in
// jobs-service/app/scheduler.py, so a hardcoded number here is wrong for any
// deployment that retunes them.
const NOTIF_CATEGORY_LABELS = {
  security: "Security alerts",
  billing: "Billing & receipts",
  product_updates: "Product updates",
  job_matches: "Job match alerts",
  suggestions: "AI suggestions",
  activity: "Account activity",
};

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export const AccountSettings = ({ open, onClose, onUpgrade, embedded = false, sections = null, isLight = false }) => {
  const showSection = (key) => !sections || sections.includes(key);
  const { user, logout, checkAuth } = useAuth();
  const isOAuthUser = user?.auth_provider === "google" || user?.auth_provider === "github";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sub, setSub] = useState(null); // { status, current_period_end, cancel_at_period_end, amount }
  const [busy, setBusy] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const [profile, setProfile] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [usage, setUsage] = useState(null);

  // --- Security ---
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [mfaSetup, setMfaSetup] = useState(null); // { secret, qr_code } while mid-setup
  const [mfaConfirmCode, setMfaConfirmCode] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);
  const [showMfaDisable, setShowMfaDisable] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const [loginHistory, setLoginHistory] = useState(null);

  // --- Notifications ---
  const [notifPrefs, setNotifPrefs] = useState(null);
  // Category list + display names as the server defines them. The rendered
  // rows used to be driven off the client's own label map, so a category the
  // server had but the client didn't ("activity") was not merely mislabelled
  // -- it never rendered at all, leaving users no way to see or change it.
  const [notifCategories, setNotifCategories] = useState(null);
  const [notifLabels, setNotifLabels] = useState(null);
  const [notifSaving, setNotifSaving] = useState("");
  const [pushStatus, setPushStatus] = useState("unknown"); // unsupported | denied | subscribed | unsubscribed
  const [pushBusy, setPushBusy] = useState(false);

  // --- Privacy ---
  const [consentSaving, setConsentSaving] = useState("");
  const [exportBusy, setExportBusy] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/payments/stripe/subscription`, { credentials: "include" });
      const data = await r.json();
      setSub(data);
    } catch {
      setSub({ status: null });
    } finally {
      setLoading(false);
    }
  };

  const loadProfile = async () => {
    try {
      const r = await fetch(`${API}/me/resume-profile`, { credentials: "include" });
      setProfile(await r.json());
    } catch {
      setProfile({ target_job_title: "", target_industry: "", years_experience: null, career_level: "", summary_style: "" });
    }
  };

  const loadUsage = async () => {
    try {
      const r = await fetch(`${API}/ai/usage`, { credentials: "include" });
      setUsage(await r.json());
    } catch {
      setUsage({ period: "", counts: {} });
    }
  };

  const loadLoginHistory = async () => {
    try {
      const r = await fetch(`${API}/auth/login-history`, { credentials: "include" });
      const data = await r.json();
      setLoginHistory(data.history || []);
    } catch {
      setLoginHistory([]);
    }
  };

  const loadNotifPrefs = async () => {
    try {
      const r = await fetch(`${API}/me/notification-preferences`, { credentials: "include" });
      const data = await r.json();
      setNotifPrefs(data.preferences || null);
      setNotifCategories(data.categories || null);
      setNotifLabels(data.labels || null);
    } catch {
      setNotifPrefs(null);
    }
  };

  const checkPushStatus = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setPushStatus("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setPushStatus("denied");
      return;
    }
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      setPushStatus(sub ? "subscribed" : "unsubscribed");
    } catch {
      setPushStatus("unsubscribed");
    }
  };

  useEffect(() => {
    if (open) {
      setConfirmingCancel(false);
      setMfaSetup(null);
      setShowMfaDisable(false);
      setCurrentPw("");
      setNewPw("");
      load();
      loadProfile();
      loadUsage();
      loadLoginHistory();
      loadNotifPrefs();
      checkPushStatus();
    }
  }, [open]);

  // On /settings this component is mounted with a literal `open` (see
  // SettingsPage.jsx), so the effect above runs once at mount and never
  // again -- subscribing from the paywall it launches left the plan card
  // and quota list showing the pre-purchase state until a manual reload,
  // i.e. a "You're on the free plan" card sitting next to a "Subscribed!"
  // toast, which reads like the payment silently failed. PaywallModal
  // announces a confirmed change and both panels refetch. Window event
  // rather than props because the paywall is a sibling, matching how
  // AuthContext already signals auth:success / auth:mfa-required.
  useEffect(() => {
    const onChanged = () => { load(); loadUsage(); };
    window.addEventListener("subscription:changed", onChanged);
    return () => window.removeEventListener("subscription:changed", onChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changePassword = async () => {
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    setPwSaving(true);
    try {
      const r = await fetch(`${API}/auth/change-password`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Could not change password");
      }
      setCurrentPw("");
      setNewPw("");
      toast.success("Password changed.");
    } catch (e) {
      toast.error(e.message || "Could not change password.");
    } finally {
      setPwSaving(false);
    }
  };

  const startMfaSetup = async () => {
    setMfaBusy(true);
    try {
      const r = await fetch(`${API}/auth/mfa/setup`, { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error("Could not start 2FA setup");
      setMfaSetup(await r.json());
    } catch (e) {
      toast.error(e.message || "Could not start 2FA setup.");
    } finally {
      setMfaBusy(false);
    }
  };

  const confirmMfaSetup = async () => {
    setMfaBusy(true);
    try {
      const r = await fetch(`${API}/auth/mfa/confirm`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: mfaConfirmCode.trim() }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Invalid code");
      }
      setMfaSetup(null);
      setMfaConfirmCode("");
      toast.success("Two-factor authentication enabled.");
      await checkAuth();
    } catch (e) {
      toast.error(e.message || "Invalid code.");
    } finally {
      setMfaBusy(false);
    }
  };

  const disableMfa = async () => {
    setMfaBusy(true);
    try {
      const r = await fetch(`${API}/auth/mfa/disable`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: disablePw }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Could not disable 2FA");
      }
      setShowMfaDisable(false);
      setDisablePw("");
      toast.success("Two-factor authentication disabled.");
      await checkAuth();
    } catch (e) {
      toast.error(e.message || "Could not disable 2FA.");
    } finally {
      setMfaBusy(false);
    }
  };

  const updateNotifPref = async (category, channel, value) => {
    const next = { ...notifPrefs, [category]: { ...notifPrefs[category], [channel]: value } };
    setNotifPrefs(next);
    setNotifSaving(`${category}.${channel}`);
    try {
      const r = await fetch(`${API}/me/notification-preferences`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ preferences: next }),
      });
      if (!r.ok) throw new Error();
    } catch {
      toast.error("Could not save notification preference.");
      loadNotifPrefs();
    } finally {
      setNotifSaving("");
    }
  };

  const enablePush = async () => {
    setPushBusy(true);
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setPushStatus("unsupported");
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setPushStatus(permission === "denied" ? "denied" : "unsubscribed");
        return;
      }
      const reg = await navigator.serviceWorker.register("/sw.js");
      const keyRes = await fetch(`${API}/me/push/vapid-public-key`, { credentials: "include" });
      const { public_key, configured } = await keyRes.json();
      if (!configured) {
        toast.error("Push notifications aren't configured on this server yet.");
        return;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
      });
      const subJson = sub.toJSON();
      await fetch(`${API}/me/push/subscribe`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ endpoint: subJson.endpoint, keys: subJson.keys }),
      });
      setPushStatus("subscribed");
      toast.success("Push notifications enabled.");
    } catch (e) {
      toast.error("Could not enable push notifications.");
    } finally {
      setPushBusy(false);
    }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        await fetch(`${API}/me/push/subscribe`, {
          method: "DELETE",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
        await sub.unsubscribe();
      }
      setPushStatus("unsubscribed");
      toast.success("Push notifications disabled.");
    } catch {
      toast.error("Could not disable push notifications.");
    } finally {
      setPushBusy(false);
    }
  };

  const updateConsent = async (field, value) => {
    setConsentSaving(field);
    try {
      const r = await fetch(`${API}/me/consent`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value }),
      });
      if (!r.ok) throw new Error();
      await checkAuth();
    } catch {
      toast.error("Could not save preference.");
    } finally {
      setConsentSaving("");
    }
  };

  const downloadMyData = async () => {
    setExportBusy(true);
    try {
      const [account, documents, billing, aiUsage, notifications] = await Promise.all([
        fetch(`${API}/me/export/account`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/me/export/documents`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/me/export/billing`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/me/export/ai-usage`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/me/export/notifications`, { credentials: "include" }).then((r) => r.json()),
      ]);
      const bundle = { exported_at: new Date().toISOString(), account, documents, billing, ai_usage: aiUsage, notifications };
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `resume-studio-data-${user?.email || "export"}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded.");
    } catch (e) {
      toast.error("Could not export your data. Please try again.");
    } finally {
      setExportBusy(false);
    }
  };

  const deleteAccount = async () => {
    if (deleteConfirmText.trim().toLowerCase() !== (user?.email || "").toLowerCase()) {
      toast.error("Type your email exactly to confirm.");
      return;
    }
    setDeleteBusy(true);
    try {
      // Each service clears its own data; auth-service goes last since that's what
      // actually revokes the account's ability to authenticate. Typing the exact
      // email above (not a password) is the confirmation step now.
      await fetch(`${API}/me/delete/documents`, { method: "DELETE", credentials: "include" });
      await fetch(`${API}/me/delete/ai-usage`, { method: "DELETE", credentials: "include" });
      await fetch(`${API}/me/delete/billing`, { method: "DELETE", credentials: "include" });
      await fetch(`${API}/me/delete/notifications`, { method: "DELETE", credentials: "include" });
      const r = await fetch(`${API}/me/delete/account`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Could not delete account");
      }
      toast.success("Your account has been deleted.");
      onClose && onClose();
      window.location.href = "/";
    } catch (e) {
      toast.error(e.message || "Could not delete your account. Please try again.");
      setDeleteBusy(false);
    }
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      const r = await fetch(`${API}/me/resume-profile`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profile),
      });
      if (!r.ok) throw new Error("Could not save career profile");
      setProfile(await r.json());
      toast.success("Career profile saved.");
    } catch (e) {
      toast.error(e.message || "Could not save career profile.");
    } finally {
      setProfileSaving(false);
    }
  };

  if (!embedded && !open) return null;

  const cancelSubscription = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/payments/stripe/subscription/cancel`, { method: "POST", credentials: "include" });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Could not cancel subscription");
      }
      const data = await r.json();
      setSub(data);
      setConfirmingCancel(false);
      toast.success("Subscription canceled. You'll keep access until your current period ends.");
    } catch (e) {
      toast.error(e.message || "Could not cancel subscription.");
    } finally {
      setBusy(false);
    }
  };

  const resumeSubscription = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/payments/stripe/subscription/resume`, { method: "POST", credentials: "include" });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Could not resume subscription");
      }
      const data = await r.json();
      setSub(data);
      toast.success("Subscription resumed — it will auto-renew as normal.");
    } catch (e) {
      toast.error(e.message || "Could not resume subscription.");
    } finally {
      setBusy(false);
    }
  };

  const changeTier = async (tier) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/payments/stripe/subscription/change-tier`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Could not change plan");
      }
      const data = await r.json();
      setSub(data);
      toast.success(`Switched to ${data.tier_label}. The price difference is prorated for the rest of this period.`);
    } catch (e) {
      toast.error(e.message || "Could not change plan.");
    } finally {
      setBusy(false);
    }
  };

  const TIER_ORDER = ["starter", "professional", "premium"];

  const isActive = sub && ["active", "trialing", "past_due"].includes(sub.status);

  // Custom toggle switch (spec'd for the notification matrix + privacy
  // consent rows) that keeps the real <input type="checkbox"> as the
  // interactive/testable element -- sr-only + peer, so testids/checked/
  // onChange/disabled all still behave exactly like a native checkbox,
  // only the visual track+thumb (an `after:` pseudo-element) is custom.
  const Toggle = ({ testid, checked, disabled, onChange }) => (
    <label className="relative inline-flex items-center justify-self-center cursor-pointer">
      <input
        data-testid={testid}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="sr-only peer"
      />
      <span
        className={`w-8 h-[18px] rounded-full transition-colors peer-checked:bg-[#A67C52] peer-disabled:opacity-50 relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-3.5 after:h-3.5 after:bg-white after:rounded-full after:shadow after:transition-transform peer-checked:after:translate-x-3.5 ${
          isLight ? "bg-slate-300" : "bg-slate-700"
        }`}
      />
    </label>
  );

  return (
    <div
      className={embedded ? "py-8 px-4" : "fixed inset-0 bg-black/85 backdrop-blur-md z-[70] flex items-center justify-center p-4"}
      data-testid="account-settings-modal"
    >
      <div className={embedded ? "max-w-2xl mx-auto space-y-5" : "bg-[#141414] border border-white/15 p-6 max-w-md w-full space-y-5 max-h-[90vh] overflow-y-auto"}>
        <div
          className={`flex justify-between items-center gap-4 ${
            embedded ? `rounded-2xl border p-5 backdrop-blur-xl shadow-xl ${isLight ? "bg-white/80 border-slate-200" : "bg-slate-900/60 border-slate-800/80"}` : ""
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            {embedded && (
              <UserAvatar user={user} size={44} className="rounded-2xl shadow-lg shadow-[#A67C52]/20" />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className={`text-base font-black ${embedded ? "" : "uppercase tracking-widest"} ${isLight ? "text-slate-900" : "text-white"}`}>
                  {embedded ? (user?.name || "Account Settings") : "Account Settings"}
                </h3>
                {embedded && isActive && (
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${
                    isLight ? "bg-[#A67C52]/10 text-[#A67C52] border-[#A67C52]/30" : "bg-[#A67C52]/10 text-[#D4A574] border-[#D4A574]/30"
                  }`}>
                    <AiIcon className="w-3 h-3" /> {sub?.tier_label || "Premium"} Plan
                  </span>
                )}
              </div>
              <p className={`text-xs mt-0.5 truncate ${isLight ? "text-slate-500" : "text-neutral-400"}`}>Signed in as {user?.email}</p>
            </div>
          </div>
          {!embedded && (
            <button data-testid="account-settings-close" onClick={onClose} className="text-neutral-500 hover:text-white flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {showSection("billing") && (
        <div className={`rounded-2xl border p-5 space-y-3 backdrop-blur-xl shadow-xl ${isLight ? "bg-white/80 border-slate-200" : "bg-slate-900/60 border-slate-800/80"}`}>
          <p className={`text-[10px] uppercase tracking-widest font-bold ${isLight ? "text-slate-500" : "text-neutral-400"}`}>Plan</p>

          {loading ? (
            <div className={`flex items-center gap-2 text-xs py-2 ${isLight ? "text-slate-500" : "text-neutral-400"}`}>
              <Loader2 className="w-4 h-4 animate-spin" /> Loading subscription…
            </div>
          ) : isActive ? (
            <div className="space-y-3" data-testid="account-subscription-active">
              <div className={`flex items-center gap-2 font-bold text-sm ${isLight ? "text-slate-900" : "text-white"}`}>
                <CheckCircle2 className="w-4 h-4 text-[#22C55E]" />
                {sub.tier_label || "Monthly"} — ${sub.amount?.toFixed ? sub.amount.toFixed(2) : sub.amount}/mo
              </div>
              {sub.cancel_at_period_end ? (
                <p className={`text-xs flex items-start gap-1.5 ${isLight ? "text-amber-600" : "text-amber-400"}`}>
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  Canceled — you'll keep full access until {fmtDate(sub.current_period_end)}, then it will not renew.
                </p>
              ) : (
                <p className={`text-xs ${isLight ? "text-slate-600" : "text-neutral-300"}`}>Renews on <span className={isLight ? "text-slate-900 font-semibold" : "text-white font-semibold"}>{fmtDate(sub.current_period_end)}</span></p>
              )}

              {!sub.cancel_at_period_end && (
                <div>
                  <p className={`text-[10px] uppercase tracking-widest font-bold mb-1.5 ${isLight ? "text-slate-500" : "text-neutral-500"}`}>Switch plan</p>
                  <div className="flex gap-1.5" data-testid="account-tier-switcher">
                    {TIER_ORDER.map((t) => (
                      <button
                        key={t}
                        data-testid={`account-switch-${t}`}
                        onClick={() => changeTier(t)}
                        disabled={busy || t === sub.tier}
                        className={`flex-1 py-2 text-[10px] font-black uppercase tracking-wider border transition-all disabled:opacity-100 ${
                          t === sub.tier
                            ? "bg-[#A67C52] text-white border-[#A67C52]"
                            : (isLight ? "bg-transparent text-slate-600 border-slate-200 hover:border-[#A67C52] disabled:hover:border-slate-200" : "bg-transparent text-neutral-300 border-white/15 hover:border-[#A67C52] disabled:hover:border-white/15")
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {sub.cancel_at_period_end ? (
                <button
                  data-testid="account-resume-subscription"
                  onClick={resumeSubscription}
                  disabled={busy}
                  className="w-full bg-[#A67C52] text-white hover:bg-[#8A6640] py-2.5 text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resume Subscription"}
                </button>
              ) : confirmingCancel ? (
                <div className="space-y-2">
                  <p className={`text-xs ${isLight ? "text-slate-600" : "text-neutral-300"}`}>Cancel? You'll keep access until {fmtDate(sub.current_period_end)}.</p>
                  <div className="flex gap-2">
                    <button
                      data-testid="account-cancel-confirm"
                      onClick={cancelSubscription}
                      disabled={busy}
                      className="flex-1 bg-[#FF3B30] text-white hover:bg-[#e6352b] py-2.5 text-xs font-black uppercase tracking-wider transition-all disabled:opacity-60"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Confirm Cancel"}
                    </button>
                    <button
                      onClick={() => setConfirmingCancel(false)}
                      disabled={busy}
                      className={`flex-1 bg-transparent border py-2.5 text-xs font-black uppercase tracking-wider transition-all disabled:opacity-60 rounded-lg ${isLight ? "border-slate-200 text-slate-600 hover:bg-slate-50" : "border-white/15 text-neutral-300 hover:bg-white/5"}`}
                    >
                      Never Mind
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  data-testid="account-cancel-subscription"
                  onClick={() => setConfirmingCancel(true)}
                  disabled={busy}
                  className="w-full bg-transparent border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white py-2.5 text-xs font-black uppercase tracking-wider transition-all disabled:opacity-60"
                >
                  Cancel Subscription
                </button>
              )}
            </div>
          ) : sub?.status === "canceled" ? (
            <div className="space-y-3" data-testid="account-subscription-ended">
              <p className={`text-xs ${isLight ? "text-slate-600" : "text-neutral-300"}`}>Your subscription has ended.</p>
              <UpgradeButton data-testid="account-resubscribe" onClick={onUpgrade} full>Resubscribe</UpgradeButton>
            </div>
          ) : (
            <div className="space-y-3" data-testid="account-subscription-none">
              <p className={`text-xs ${isLight ? "text-slate-600" : "text-neutral-300"}`}>
                {user?.has_download_access
                  ? <>You have Starter access until {fmtDate(user.download_access_until)} (one-time pass, no subscription). Subscribe to keep it after that.</>
                  : "You're on the free plan."}
              </p>
              <UpgradeButton data-testid="account-upgrade" onClick={onUpgrade} full>See plans &amp; upgrade</UpgradeButton>
            </div>
          )}
        </div>
        )}

        {showSection("profile") && (
        <div className={`rounded-2xl border p-5 space-y-3 backdrop-blur-xl shadow-xl ${isLight ? "bg-white/80 border-slate-200" : "bg-slate-900/60 border-slate-800/80"}`} data-testid="account-avatar">
          <p className={`text-[10px] uppercase tracking-widest font-bold ${isLight ? "text-slate-500" : "text-neutral-400"}`}>Avatar</p>
          <AvatarPicker isLight={isLight} />
        </div>
        )}

        {showSection("profile") && (
        <div className={`rounded-2xl border p-5 space-y-3 backdrop-blur-xl shadow-xl ${isLight ? "bg-white/80 border-slate-200" : "bg-slate-900/60 border-slate-800/80"}`} data-testid="account-career-profile">
          <p className={`text-[10px] uppercase tracking-widest font-bold ${isLight ? "text-slate-500" : "text-neutral-400"}`}>Career Profile</p>
          <p className={`text-[11px] -mt-2 ${isLight ? "text-slate-500" : "text-neutral-500"}`}>Steers AI summaries, cover letters, and style suggestions.</p>
          {profile && (
            <div className="space-y-2.5">
              <input
                data-testid="profile-target-title"
                value={profile.target_job_title || ""}
                onChange={(e) => setProfile({ ...profile, target_job_title: e.target.value })}
                placeholder="Target job title (e.g. Senior DevOps Engineer)"
                className={`w-full border outline-none px-3.5 py-2.5 text-xs rounded-xl transition-colors ${isLight ? "bg-white border-slate-200 focus:border-[#A67C52] focus:ring-2 focus:ring-[#A67C52]/20 text-slate-900 placeholder:text-slate-500" : "bg-slate-800/60 border-slate-700/80 focus:border-[#D4A574] focus:ring-2 focus:ring-[#D4A574]/20 text-slate-100 placeholder:text-neutral-600"}`}
              />
              <input
                data-testid="profile-target-industry"
                value={profile.target_industry || ""}
                onChange={(e) => setProfile({ ...profile, target_industry: e.target.value })}
                placeholder="Target industry (e.g. Cloud Infrastructure)"
                className={`w-full border outline-none px-3.5 py-2.5 text-xs rounded-xl transition-colors ${isLight ? "bg-white border-slate-200 focus:border-[#A67C52] focus:ring-2 focus:ring-[#A67C52]/20 text-slate-900 placeholder:text-slate-500" : "bg-slate-800/60 border-slate-700/80 focus:border-[#D4A574] focus:ring-2 focus:ring-[#D4A574]/20 text-slate-100 placeholder:text-neutral-600"}`}
              />
              <div className="grid grid-cols-2 gap-2.5">
                <input
                  data-testid="profile-years-experience"
                  type="number"
                  min="0"
                  max="60"
                  value={profile.years_experience ?? ""}
                  onChange={(e) => setProfile({ ...profile, years_experience: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                  placeholder="Years experience"
                  className={`w-full border outline-none px-3.5 py-2.5 text-xs rounded-xl transition-colors ${isLight ? "bg-white border-slate-200 focus:border-[#A67C52] focus:ring-2 focus:ring-[#A67C52]/20 text-slate-900 placeholder:text-slate-500" : "bg-slate-800/60 border-slate-700/80 focus:border-[#D4A574] focus:ring-2 focus:ring-[#D4A574]/20 text-slate-100 placeholder:text-neutral-600"}`}
                />
                <select
                  data-testid="profile-career-level"
                  value={profile.career_level || ""}
                  onChange={(e) => setProfile({ ...profile, career_level: e.target.value })}
                  className={`w-full border outline-none px-3.5 py-2.5 text-xs rounded-xl transition-colors ${isLight ? "bg-white border-slate-200 focus:border-[#A67C52] focus:ring-2 focus:ring-[#A67C52]/20 text-slate-900" : "bg-slate-800/60 border-slate-700/80 focus:border-[#D4A574] focus:ring-2 focus:ring-[#D4A574]/20 text-slate-100"}`}
                >
                  <option value="">Career level…</option>
                  {CAREER_LEVELS.map((l) => (
                    <option key={l} value={l}>{l[0].toUpperCase() + l.slice(1)}</option>
                  ))}
                </select>
              </div>
              <select
                data-testid="profile-summary-style"
                value={profile.summary_style || ""}
                onChange={(e) => setProfile({ ...profile, summary_style: e.target.value })}
                className={`w-full border outline-none px-3.5 py-2.5 text-xs rounded-xl transition-colors ${isLight ? "bg-white border-slate-200 focus:border-[#A67C52] focus:ring-2 focus:ring-[#A67C52]/20 text-slate-900" : "bg-slate-800/60 border-slate-700/80 focus:border-[#D4A574] focus:ring-2 focus:ring-[#D4A574]/20 text-slate-100"}`}
              >
                <option value="">AI summary style…</option>
                {SUMMARY_STYLES.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
              <button
                data-testid="profile-save"
                onClick={saveProfile}
                disabled={profileSaving}
                className="w-full rounded-xl bg-gradient-to-r from-[#A67C52] to-[#3D9970] hover:from-[#8A6640] hover:to-[#3D9970] text-white shadow-lg shadow-[#A67C52]/20 py-3 text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-60"
              >
                {profileSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5" /> Save Career Profile</>}
              </button>
            </div>
          )}
        </div>
        )}

        {showSection("usage") && usage && (
          <div className={`rounded-2xl border p-5 space-y-3 backdrop-blur-xl shadow-xl ${isLight ? "bg-white/80 border-slate-200" : "bg-slate-900/60 border-slate-800/80"}`} data-testid="account-ai-usage">
            <p className={`text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 ${isLight ? "text-slate-500" : "text-neutral-400"}`}>
              <BarChart3 className="w-3 h-3" /> AI Usage This Month {usage.tier ? `— ${usage.tier[0].toUpperCase()}${usage.tier.slice(1)} ${sub?.status && ["active", "trialing", "past_due"].includes(sub.status) ? "plan" : "access"}` : "— Free plan"}
            </p>
            <ul className="space-y-2.5">
              {Object.entries(usage.limits || {}).map(([key, limit]) => {
                const used = usage.counts?.[key] || 0;
                const unlimited = limit === -1;
                // limit 0 means the feature isn't part of this tier at all --
                // a plan boundary, not a quota the user burned through. It was
                // rendering as a red "0 / 0" at-limit row, which reads as
                // "you've hit your cap" (or as a bug) on a plan that simply
                // never included it. Only a real cap that's been reached earns
                // the red treatment.
                const notIncluded = limit === 0;
                const pct = unlimited || notIncluded ? 0 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
                const atLimit = !unlimited && !notIncluded && used >= limit;
                return (
                  <li key={key} className="space-y-1">
                    <div className="flex justify-between text-[11px]">
                      <span className={notIncluded ? (isLight ? "text-slate-500" : "text-neutral-500") : isLight ? "text-slate-600" : "text-neutral-300"}>
                        {usage.labels?.[key] || USAGE_LABELS[key] || key}
                      </span>
                      {notIncluded ? (
                        <span className={`font-semibold ${isLight ? "text-slate-500" : "text-neutral-500"}`}>Not on this plan</span>
                      ) : (
                        <span className={`font-bold rounded-full px-2 py-0.5 text-[10px] ${
                          atLimit
                            ? "bg-red-500/10 text-red-500 border border-red-500/30"
                            : isLight ? "bg-[#A67C52]/10 text-[#A67C52] border border-[#A67C52]/20" : "bg-[#A67C52]/10 text-[#D4A574] border border-[#D4A574]/20"
                        }`}>
                          {used} / {unlimited ? "∞" : limit}
                        </span>
                      )}
                    </div>
                    {!unlimited && !notIncluded && (
                      <div className={`h-1.5 rounded-full overflow-hidden ${isLight ? "bg-slate-200" : "bg-white/10"}`}>
                        <div
                          className={`h-full rounded-full transition-all ${atLimit ? "bg-red-500" : "bg-gradient-to-r from-[#A67C52] to-[#3D9970]"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            {!usage.tier && (
              <UpgradeButton data-testid="account-usage-upgrade" onClick={onUpgrade} variant="outline" size="sm" full className="mt-1">Upgrade for higher AI limits</UpgradeButton>
            )}
          </div>
        )}

        {showSection("security") && (
        <div className={`rounded-2xl border p-5 space-y-4 backdrop-blur-xl shadow-xl ${isLight ? "bg-white/80 border-slate-200" : "bg-slate-900/60 border-slate-800/80"}`} data-testid="account-security">
          <p className={`text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 ${isLight ? "text-slate-500" : "text-neutral-400"}`}>
            <ShieldCheck className="w-3 h-3" /> Security
          </p>

          {!isOAuthUser && (
            <div className="space-y-2" data-testid="account-change-password">
              <p className={`text-[11px] font-semibold ${isLight ? "text-slate-500" : "text-neutral-500"}`}>Change Password</p>
              <input
                data-testid="account-current-password"
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                placeholder="Current password"
                className={`w-full border outline-none px-3.5 py-2.5 text-xs rounded-xl transition-colors ${isLight ? "bg-white border-slate-200 focus:border-[#A67C52] focus:ring-2 focus:ring-[#A67C52]/20 text-slate-900 placeholder:text-slate-500" : "bg-slate-800/60 border-slate-700/80 focus:border-[#D4A574] focus:ring-2 focus:ring-[#D4A574]/20 text-slate-100 placeholder:text-neutral-600"}`}
              />
              <input
                data-testid="account-new-password"
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="New password (min 8 characters)"
                className={`w-full border outline-none px-3.5 py-2.5 text-xs rounded-xl transition-colors ${isLight ? "bg-white border-slate-200 focus:border-[#A67C52] focus:ring-2 focus:ring-[#A67C52]/20 text-slate-900 placeholder:text-slate-500" : "bg-slate-800/60 border-slate-700/80 focus:border-[#D4A574] focus:ring-2 focus:ring-[#D4A574]/20 text-slate-100 placeholder:text-neutral-600"}`}
              />
              <button
                data-testid="account-change-password-submit"
                onClick={changePassword}
                disabled={pwSaving || !currentPw || newPw.length < 8}
                className={`w-full bg-transparent border py-2 text-[11px] font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-60 rounded-lg ${isLight ? "border-slate-200 hover:border-[#A67C52] text-slate-600 hover:text-[#A67C52] hover:bg-slate-50" : "border-white/15 hover:border-[#A67C52] text-neutral-300 hover:text-[#A67C52]"}`}
              >
                {pwSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><KeyRound className="w-3.5 h-3.5" /> Update Password</>}
              </button>
            </div>
          )}

          <div className={`space-y-2 pt-3 border-t ${isLight ? "border-slate-200" : "border-white/10"}`} data-testid="account-mfa">
            <p className={`text-[11px] font-semibold ${isLight ? "text-slate-500" : "text-neutral-500"}`}>Two-Factor Authentication</p>
            {user?.mfa_enabled ? (
              showMfaDisable ? (
                <div className="space-y-2">
                  <input
                    data-testid="account-mfa-disable-password"
                    type="password"
                    value={disablePw}
                    onChange={(e) => setDisablePw(e.target.value)}
                    placeholder="Confirm your password"
                    className={`w-full border outline-none px-3.5 py-2.5 text-xs rounded-xl transition-colors ${isLight ? "bg-white border-slate-200 focus:border-[#A67C52] focus:ring-2 focus:ring-[#A67C52]/20 text-slate-900 placeholder:text-slate-500" : "bg-slate-800/60 border-slate-700/80 focus:border-[#D4A574] focus:ring-2 focus:ring-[#D4A574]/20 text-slate-100 placeholder:text-neutral-600"}`}
                  />
                  <div className="flex gap-2">
                    <button
                      data-testid="account-mfa-disable-confirm"
                      onClick={disableMfa}
                      disabled={mfaBusy || !disablePw}
                      className="flex-1 bg-[#FF3B30] text-white hover:bg-[#e6352b] py-2 text-[11px] font-black uppercase tracking-wider transition-all disabled:opacity-60"
                    >
                      {mfaBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : "Disable 2FA"}
                    </button>
                    <button onClick={() => { setShowMfaDisable(false); setDisablePw(""); }} className={`flex-1 bg-transparent border py-2 text-[11px] font-black uppercase tracking-wider transition-all rounded-lg ${isLight ? "border-slate-200 text-slate-600 hover:bg-slate-50" : "border-white/15 text-neutral-300 hover:bg-white/5"}`}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#22C55E] flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Enabled</span>
                  <button data-testid="account-mfa-disable-start" onClick={() => setShowMfaDisable(true)} className="text-[11px] text-[#FF3B30] hover:underline font-bold uppercase tracking-wider">Disable</button>
                </div>
              )
            ) : mfaSetup ? (
              <div className="space-y-2.5" data-testid="account-mfa-setup">
                <p className={`text-[11px] ${isLight ? "text-slate-500" : "text-neutral-400"}`}>Scan with your authenticator app (Google Authenticator, Authy, 1Password…):</p>
                <img src={mfaSetup.qr_code} alt="MFA QR code" className="w-36 h-36 mx-auto bg-white p-2 rounded" />
                <p className={isLight ? "text-[10px] text-slate-400 text-center font-mono break-all" : "text-[10px] text-neutral-500 text-center font-mono break-all"}>Or enter manually: {mfaSetup.secret}</p>
                <input
                  data-testid="account-mfa-confirm-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={mfaConfirmCode}
                  onChange={(e) => setMfaConfirmCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="Enter 6-digit code to confirm"
                  className={`w-full border outline-none px-3 py-2 text-xs rounded-lg text-center tracking-[0.3em] placeholder:tracking-normal ${isLight ? "bg-slate-50 border-slate-200 focus:border-[#A67C52] text-slate-900 placeholder:text-slate-500" : "bg-black/40 border-white/10 focus:border-[#A67C52] text-white placeholder:text-neutral-600"}`}
                />
                <div className="flex gap-2">
                  <button
                    data-testid="account-mfa-confirm-submit"
                    onClick={confirmMfaSetup}
                    disabled={mfaBusy || mfaConfirmCode.length !== 6}
                    className="flex-1 bg-[#A67C52] text-white hover:bg-[#8A6640] py-2 text-[11px] font-black uppercase tracking-wider transition-all disabled:opacity-60"
                  >
                    {mfaBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : "Confirm & Enable"}
                  </button>
                  <button onClick={() => { setMfaSetup(null); setMfaConfirmCode(""); }} className={`flex-1 bg-transparent border py-2 text-[11px] font-black uppercase tracking-wider transition-all rounded-lg ${isLight ? "border-slate-200 text-slate-600 hover:bg-slate-50" : "border-white/15 text-neutral-300 hover:bg-white/5"}`}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className={isLight ? "text-xs text-slate-500" : "text-xs text-neutral-500"}>Not enabled</span>
                <button data-testid="account-mfa-enable-start" onClick={startMfaSetup} disabled={mfaBusy} className="text-[11px] text-[#A67C52] hover:underline font-bold uppercase tracking-wider disabled:opacity-60">
                  {mfaBusy ? "Loading…" : "Enable 2FA"}
                </button>
              </div>
            )}
          </div>

          {loginHistory && loginHistory.length > 0 && (
            <div className={`space-y-2 pt-3 border-t ${isLight ? "border-slate-200" : "border-white/10"}`} data-testid="account-login-history">
              <p className={`text-[11px] font-semibold flex items-center gap-1.5 ${isLight ? "text-slate-500" : "text-neutral-500"}`}><Clock className="w-3 h-3" /> Recent Logins</p>
              <ul className={`space-y-1.5 max-h-32 overflow-y-auto font-mono text-[10px] p-3 rounded-xl border ${isLight ? "bg-slate-50 border-slate-200 text-slate-500" : "bg-slate-950/80 border-slate-800 text-slate-500"}`}>
                {loginHistory.slice(0, 8).map((h, i) => (
                  <li key={i} className="flex justify-between">
                    <span>{h.method} · {h.ip}</span>
                    <span>{fmtDateTime(h.created_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        )}

        {showSection("notifications") && (
        <div className={`rounded-2xl border p-5 space-y-4 backdrop-blur-xl shadow-xl ${isLight ? "bg-white/80 border-slate-200" : "bg-slate-900/60 border-slate-800/80"}`} data-testid="account-notifications">
          <p className={`text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 ${isLight ? "text-slate-500" : "text-neutral-400"}`}>
            <Bell className="w-3 h-3" /> Notifications
          </p>

          {notifPrefs && (
            <div className="space-y-3">
              <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-2 items-center">
                <span />
                <span className={`text-[9px] uppercase tracking-wider flex items-center gap-1 justify-center ${isLight ? "text-slate-500" : "text-neutral-500"}`}><Mail className="w-3 h-3" /> Email</span>
                <span className={`text-[9px] uppercase tracking-wider flex items-center gap-1 justify-center ${isLight ? "text-slate-500" : "text-neutral-500"}`}><Smartphone className="w-3 h-3" /> Push</span>
                {/* Server's category list drives the rows, so a category it
                    adds can't go missing here; labels prefer the server's,
                    falling back to the local map then a readable key. */}
                {(notifCategories || Object.keys(NOTIF_CATEGORY_LABELS)).map((cat) => (
                  <React.Fragment key={cat}>
                    <span className={`text-xs ${isLight ? "text-slate-600" : "text-neutral-300"}`}>
                      {notifLabels?.[cat] || NOTIF_CATEGORY_LABELS[cat] || cat.replace(/_/g, " ")}
                    </span>
                    <Toggle
                      testid={`notif-pref-${cat}-email`}
                      checked={!!notifPrefs[cat]?.email}
                      disabled={notifSaving === `${cat}.email`}
                      onChange={(e) => updateNotifPref(cat, "email", e.target.checked)}
                    />
                    <Toggle
                      testid={`notif-pref-${cat}-push`}
                      checked={!!notifPrefs[cat]?.push}
                      disabled={notifSaving === `${cat}.push` || pushStatus !== "subscribed"}
                      onChange={(e) => updateNotifPref(cat, "push", e.target.checked)}
                    />
                  </React.Fragment>
                ))}
              </div>
              <p className={`text-[11px] ${isLight ? "text-slate-500" : "text-neutral-500"}`} data-testid="job-match-alert-threshold-note">
                Job match alerts fire for postings that score 80%+ against the resume attached to that saved search, and a lighter
                nudge for the 55–79% range suggesting you run the ATS Score Analyzer to tune your resume before applying — no flood
                of low-fit listings below that, email or push.
              </p>
            </div>
          )}

          <div className={`pt-2 border-t ${isLight ? "border-slate-200" : "border-white/10"}`}>
            {pushStatus === "subscribed" ? (
              <button
                data-testid="push-disable-button"
                onClick={disablePush}
                disabled={pushBusy}
                className={`w-full bg-transparent border py-2.5 text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-60 rounded-lg ${isLight ? "border-slate-200 hover:border-[#FF3B30] text-slate-600 hover:text-[#FF3B30] hover:bg-red-50" : "border-white/15 hover:border-[#FF3B30] text-neutral-300 hover:text-[#FF3B30]"}`}
              >
                {pushBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><BellOff className="w-3.5 h-3.5" /> Disable Push Notifications</>}
              </button>
            ) : pushStatus === "unsupported" ? (
              <p className={`text-[11px] ${isLight ? "text-slate-500" : "text-neutral-500"}`}>Push notifications aren't supported in this browser.</p>
            ) : pushStatus === "denied" ? (
              <p className={`text-[11px] ${isLight ? "text-slate-500" : "text-neutral-500"}`}>Push notifications are blocked for this site in your browser settings.</p>
            ) : (
              <button
                data-testid="push-enable-button"
                onClick={enablePush}
                disabled={pushBusy}
                className={`w-full bg-transparent border py-2.5 text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-60 rounded-lg ${isLight ? "border-slate-200 hover:border-[#A67C52] text-slate-600 hover:text-[#A67C52] hover:bg-slate-50" : "border-white/15 hover:border-[#A67C52] text-neutral-300 hover:text-[#A67C52]"}`}
              >
                {pushBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Bell className="w-3.5 h-3.5" /> Enable Push Notifications</>}
              </button>
            )}
          </div>
        </div>
        )}

        {showSection("privacy") && (
        <div className={`rounded-2xl border p-5 space-y-4 backdrop-blur-xl shadow-xl ${isLight ? "bg-white/80 border-slate-200" : "bg-slate-900/60 border-slate-800/80"}`} data-testid="account-privacy">
          <p className={`text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 ${isLight ? "text-slate-500" : "text-neutral-400"}`}>
            <Lock className="w-3 h-3" /> Privacy
          </p>

          <div className="space-y-2.5">
            <div className={`flex items-center justify-between text-xs ${isLight ? "text-slate-600" : "text-neutral-300"}`}>
              <span>Marketing emails</span>
              <Toggle
                testid="account-consent-marketing"
                checked={!!user?.marketing_consent}
                disabled={consentSaving === "marketing_consent"}
                onChange={(e) => updateConsent("marketing_consent", e.target.checked)}
              />
            </div>
            <div className={`flex items-center justify-between text-xs ${isLight ? "text-slate-600" : "text-neutral-300"}`}>
              <span>Allow my content to improve AI features</span>
              <Toggle
                testid="account-consent-ai-training"
                checked={!!user?.ai_training_consent}
                disabled={consentSaving === "ai_training_consent"}
                onChange={(e) => updateConsent("ai_training_consent", e.target.checked)}
              />
            </div>
          </div>

          <button
            data-testid="account-download-data"
            onClick={downloadMyData}
            disabled={exportBusy}
            className={`w-full bg-transparent border py-2.5 text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-60 rounded-lg ${isLight ? "border-slate-200 hover:border-[#A67C52] text-slate-600 hover:text-[#A67C52] hover:bg-slate-50" : "border-white/15 hover:border-[#A67C52] text-neutral-300 hover:text-[#A67C52]"}`}
          >
            {exportBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5" /> Download My Data</>}
          </button>

          {!showDeleteConfirm ? (
            <button
              data-testid="account-delete-start"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 py-2.5 text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete Account
            </button>
          ) : (
            <div className="space-y-2 rounded-xl border border-red-500/30 bg-red-500/5 p-3" data-testid="account-delete-confirm">
              <p className="text-xs text-[#FF3B30] font-bold flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                This permanently deletes all resumes, cover letters, certificates, your subscription, and login — it cannot be undone.
              </p>
              <p className={`text-[11px] ${isLight ? "text-slate-500" : "text-neutral-400"}`}>Type your email (<span className={isLight ? "text-slate-900 font-mono" : "text-white font-mono"}>{user?.email}</span>) to confirm:</p>
              <input
                data-testid="account-delete-email-confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={user?.email}
                className={`w-full border outline-none px-3.5 py-2.5 text-xs rounded-xl transition-colors ${isLight ? "bg-white border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 text-slate-900 placeholder:text-slate-500" : "bg-slate-800/60 border-slate-700/80 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 text-slate-100 placeholder:text-neutral-600"}`}
              />
              <div className="flex gap-2">
                <button
                  data-testid="account-delete-confirm-submit"
                  onClick={deleteAccount}
                  disabled={
                    deleteBusy ||
                    deleteConfirmText.trim().toLowerCase() !== (user?.email || "").toLowerCase()
                  }
                  className="flex-1 bg-[#FF3B30] text-white hover:bg-[#e6352b] py-2.5 text-xs font-black uppercase tracking-wider transition-all disabled:opacity-60"
                >
                  {deleteBusy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Permanently Delete"}
                </button>
                <button
                  onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(""); }}
                  disabled={deleteBusy}
                  className={`flex-1 bg-transparent border py-2.5 text-xs font-black uppercase tracking-wider transition-all disabled:opacity-60 rounded-lg ${isLight ? "border-slate-200 text-slate-600 hover:bg-slate-50" : "border-white/15 text-neutral-300 hover:bg-white/5"}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        <button
          data-testid="account-settings-logout"
          onClick={async () => { await logout(); onClose && onClose(); navigate("/"); }}
          className="w-full rounded-xl border border-amber-500/30 text-amber-500 hover:bg-amber-500/10 py-2.5 text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign Out
        </button>
      </div>
    </div>
  );
};

export default AccountSettings;
